// javascript
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useTextDomainContext } from "@/app/providers";
import { useDropzone } from "react-dropzone";
import type { Accept, FileWithPath } from "react-dropzone";
import { object, array, func, bool, string } from "prop-types";
import { TbFileUpload } from "react-icons/tb";
import { CiCircleInfo } from "react-icons/ci";
import { FileIcon } from "../Images";
import { RxCross2 } from "react-icons/rx";
import { formatFileSize } from "@/utils";

type Dropzone2Props = {
  accept?: Accept;
  progress?: number[];
  getSelected?: (files: FileWithPath[]) => void;
  multiple?: boolean;
  description?: string;
  componentLabel?: string;
};

/**
 * A file dropzone. It will use to select files for upload.
 * @param props
 * @returns {React.JSX.Element}
 */
export const Dropzone2 = (props: Dropzone2Props) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const description = useMemo(() => {
    return props.description || gettext("Max size supported 20MB");
  }, [gettext, props.description]);

  const accept = useMemo(() => {
    const defaultAccept = {
      "image/png": [".png"],
      "image/jpeg": [".jpeg", ".jpg"],
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/x-pdf": [".pdf"],
    };
    return props.accept || defaultAccept;
  }, [props.accept]);

  const progress = useMemo(() => {
    return props.progress || [];
  }, [props.progress]);

  const [selectedFiles, setSelectedFiles] = useState<FileWithPath[]>([]);
  const [highlighted, setHighlighted] = useState(false);

  // `multiple` prop controls single vs multiple file selection
  const multiple = typeof props.multiple === "boolean" ? props.multiple : false;

  const { getSelected } = props;
  useEffect(() => {
    if (typeof getSelected === "function") {
      getSelected(selectedFiles);
    }
  }, [getSelected, selectedFiles]);

  // show dropzone when multiple mode OR no file selected (single mode & file selected -> hide)
  const showDropzone = multiple || selectedFiles.length === 0;

  // dropzone height in pixels (compact vs expanded)
  const dropzoneHeight = selectedFiles.length ? 150 : 52;

  const onDrop = useCallback(
    <T extends File>(acceptedFilesRaw: T[]) => {
      const files = acceptedFilesRaw as unknown as FileWithPath[];

      if (!multiple) {
        // single-file mode: replace existing with the first accepted file
        const newFiles = files.slice(0, 1);
        setSelectedFiles(newFiles);
        return;
      }

      // multiple mode: merge and dedupe by name/path
      const allMergedFiles = [...selectedFiles, ...files];
      const allSelectedFiles = allMergedFiles.filter(
        (file, index, self) =>
          index ===
          self.findIndex(
            (f) => (f.name || f.path) === (file.name || file.path),
          ),
      );

      setSelectedFiles(allSelectedFiles);
    },
    [selectedFiles, multiple],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: accept,
    multiple: multiple,
  });

  const handleDelete = (file: FileWithPath) => {
    const allSelectedFiles = selectedFiles.filter(
      (fileObj) => fileObj !== file,
    );
    setSelectedFiles(allSelectedFiles);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {props.componentLabel ? (
        <Typography sx={{ mb: 1, fontWeight: 600 }}>
          {props.componentLabel}
        </Typography>
      ) : null}
      {showDropzone && (
        <Box
          sx={{
            p: 1,
            backgroundColor: "grey.50",
            backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='3' ry='3' stroke='%23333' stroke-width='3' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`,
            minHeight: 250,
            borderRadius: "3px",
            cursor: "pointer",
            height: `${dropzoneHeight}px`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
          {...getRootProps({
            className: `p-16 my-10 border text-center ${
              highlighted ? "border-primary" : "border-secondary "
            }`,
          })}
          onDragEnter={() => {
            setHighlighted(true);
          }}
          onDragLeave={() => {
            setHighlighted(false);
          }}
        >
          <TbFileUpload size={46} />
          <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
            <input {...getInputProps({ multiple: multiple })} />
            {gettext("Click to upload or drag and drop")}
            <br />
            <CiCircleInfo size={12} />
            &nbsp;
            {description}
          </Typography>
        </Box>
      )}

      {selectedFiles?.length > 0 && (
        <Box sx={{ textAlign: "left", mt: 1 }}>
          <Typography sx={{ lineHeight: "40px", fontWeight: 500 }}>
            {gettext("Uploading")}&nbsp;
            {selectedFiles?.length}&nbsp;
            {selectedFiles?.length === 1 ? gettext("Item") : gettext("Items")}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          p: 1,
          minHeight: "52px",
          overflow: "auto",
          textAlign: "left",
          scrollbarWidth: "thin",
          flex: 1,
        }}
      >
        <Box sx={{ width: "100%" }}>
          {selectedFiles.map((file, index) => {
            const name = file.name || file.path || `file-${index}`;
            const mime = file.type || "";
            const size =
              typeof file.size === "number" ? formatFileSize(file.size) : "";
            const percent = progress[index] ? progress[index] : 0;
            return (
              <Box
                key={`${name}-${index}`}
                sx={{
                  borderRadius: 1,
                  border: "2px solid",
                  borderColor: "grey.200",
                  backgroundColor: "common.white",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.9rem",
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    width: 64,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileIcon type={mime} filename={name} />
                </Box>

                <Box
                  sx={{
                    py: 1,
                    px: 1,
                    overflow: "hidden",
                    width: "100%",
                  }}
                >
                  <Grid container spacing={0} sx={{ alignItems: "center" }}>
                    <Grid
                      size={{ xs: 9 }}
                      title={name}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography noWrap sx={{ fontWeight: 600 }}>
                        {name}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ color: "text.secondary" }}
                      >
                        {mime}
                        {mime && size ? " • " : ""}
                        {size}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 2 }} sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {percent}%
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 1 }} sx={{ textAlign: "right" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(file)}
                        aria-label={gettext("Remove file")}
                        sx={{ ml: 1 }}
                      >
                        <RxCross2 size={16} />
                      </IconButton>
                    </Grid>

                    <Grid size={{ xs: 12 }} sx={{ pt: 1 }}>
                      <LinearProgress
                        variant={"determinate"}
                        value={percent}
                        color="success"
                        aria-valuenow={percent}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

Dropzone2.propTypes = {
  accept: object,
  progress: array,
  getSelected: func,
  multiple: bool,
  description: string,
  componentLabel: string,
};

Dropzone2.defaultProps = {
  multiple: false,
};
